export const CURRENCY_CODES = [
    'EUR',
    'USD',
    'GBP',
    'TWD',
    'MYR',
    'SGD',
    'CRC',
    'CLP',
    'COP',
    'PAB',
    'JPY',
    'CNY',
    'AUD',
    'CAD',
    'CHF',
    'SEK',
    'NOK',
    'DKK',
    'NZD',
    'BRL',
    'ARS',
    'MXN',
    'PLN',
    'INR',
    'RUB',
    'ZAR',
    'KRW',
    'TRY',
    'HKD'
] as const;

export type CurrencyCode = (typeof CURRENCY_CODES)[number];
export const DEFAULT_CURRENCY_CODE: CurrencyCode = 'EUR';

export type SymbolPosition = 'left' | 'right';

export const NO_DECIMAL_COUNTRY_CODES = ['TW', 'CL'] as const;

export const CURRENCY_SYMBOLS: Record<string, string> = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    AUD: 'A$',
    CAD: 'CA$',
    CHF: 'CHF',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    NZD: 'NZ$',
    BRL: 'R$',
    ARS: '$',
    MXN: 'Mex$',
    PLN: 'zł',
    INR: '₹',
    RUB: '₽',
    ZAR: 'R',
    KRW: '₩',
    TRY: '₺',
    HKD: 'HK$',
    SGD: 'S$',
    TWD: 'NT$',
    MYR: 'RM',
    CRC: '$',
    CLP: '$',
    COP: 'COL$',
    PAB: 'B/.'
};

const SYMBOL_TO_CURRENCY: Record<string, string[]> = (() => {
    const m: Record<string, string[]> = {};
    for (const [code, sym] of Object.entries(CURRENCY_SYMBOLS)) {
        const arr = m[sym] ?? [];
        arr.push(code);
        m[sym] = arr;
    }
    m['$'] = ['USD', 'CRC', 'MXN', 'ARS', 'CLP', 'COP'];
    return m;
})();

export function normalizeCurrencyCode(code?: string | null): string | undefined {
    if (!code) return undefined;
    const c = String(code).trim().toUpperCase();
    return c.length === 3 ? c : undefined;
}

export function normalizeCurrencyCodeStrict(code?: string | null): CurrencyCode | undefined {
    const normalized = normalizeCurrencyCode(code);
    if (!normalized) return undefined;
    return (CURRENCY_CODES as readonly string[]).includes(normalized)
        ? (normalized as CurrencyCode)
        : undefined;
}

export function isKnownCurrencyCode(code?: string | null): code is CurrencyCode {
    return !!normalizeCurrencyCodeStrict(code);
}

export function normalizeCurrencyForFormatting(code?: string | null): string | undefined {
    const normalized = normalizeCurrencyCode(code);
    if (normalized === 'CRC') return 'USD';
    return normalized;
}

export function getCurrencySymbol(code?: string | null): string {
    const normalized = normalizeCurrencyCode(code);
    if (!normalized) return '';
    return CURRENCY_SYMBOLS[normalized] ?? normalized;
}

export function registerCurrencySymbol(code: string, symbol: string): void {
    const normalized = normalizeCurrencyCode(code);
    if (!normalized || !symbol) return;
    CURRENCY_SYMBOLS[normalized] = symbol;
    const arr = SYMBOL_TO_CURRENCY[symbol] ?? [];
    if (!arr.includes(normalized)) arr.unshift(normalized);
    SYMBOL_TO_CURRENCY[symbol] = arr;
}

export function inferCurrencyCodeFromSymbol(symbol?: string): string | undefined {
    if (!symbol) return undefined;
    const candidates = SYMBOL_TO_CURRENCY[symbol];
    if (!candidates || candidates.length === 0) return undefined;
    return candidates[0];
}

export const DEFAULT_LANG_TO_LOCALE: Record<string, string> = {
    es: 'es-ES',
    en: 'en-US',
    fr: 'fr-FR',
    de: 'de-DE',
    zh: 'zh-CN',
    cht: 'zh-Hant',
    pt: 'pt-PT',
    it: 'it-IT',
    nl: 'nl-NL',
    sv: 'sv-SE',
    no: 'nb-NO',
    ja: 'ja-JP',
    ko: 'ko-KR'
};

export const CURRENCY_TO_REPRESENTATIVE_LOCALE: Record<string, string> = {
    EUR: 'es-ES',
    USD: 'en-US',
    GBP: 'en-GB',
    JPY: 'ja-JP',
    CNY: 'zh-CN',
    AUD: 'en-AU',
    CAD: 'en-CA',
    CHF: 'de-CH',
    SEK: 'sv-SE',
    NOK: 'nb-NO',
    DKK: 'da-DK',
    NZD: 'en-NZ',
    BRL: 'pt-BR',
    ARS: 'es-AR',
    MXN: 'es-MX',
    PLN: 'pl-PL',
    INR: 'hi-IN',
    RUB: 'ru-RU',
    ZAR: 'en-ZA',
    KRW: 'ko-KR',
    TRY: 'tr-TR',
    HKD: 'zh-HK',
    SGD: 'en-SG',
    TWD: 'zh-TW',
    MYR: 'ms-MY',
    CLP: 'es-CL',
    COP: 'es-CO',
    PAB: 'es-PA',
    CRC: 'es-CR'
};

let currentCurrencyDisplayCountry: string | undefined;

export function setCurrencyDisplayCountry(country?: string | null): void {
    if (!country) {
        currentCurrencyDisplayCountry = undefined;
        return;
    }

    const normalized = country
        .toString()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

    currentCurrencyDisplayCountry = normalized || undefined;
}

export function shouldUseZeroDecimalsForCountry(country?: string | null): boolean {
    const raw = country ?? currentCurrencyDisplayCountry;
    if (!raw) return false;

    const normalized = raw
        .toString()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

    return normalized === 'TW' || normalized === 'TAIWAN' || normalized === 'CL' || normalized === 'CHILE';
}

export function resolveLocale(locale?: string | null, lang?: string | null): string {
    if (locale && locale.trim()) {
        return locale;
    }
    if (lang && lang.includes('-')) {
        return lang;
    }
    if (lang && DEFAULT_LANG_TO_LOCALE[lang]) {
        return DEFAULT_LANG_TO_LOCALE[lang];
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
        return navigator.language;
    }
    return 'en-US';
}

export function detectCurrencySymbolPosition(currencyCode?: string | null, locale?: string | null): SymbolPosition {
    const normalized = normalizeCurrencyCode(currencyCode);
    if (!normalized) return 'left';

    const representativeLocale = CURRENCY_TO_REPRESENTATIVE_LOCALE[normalized];
    const preferredLocale = locale && locale.trim()
        ? locale
        : representativeLocale;

    const formattingCode = normalized === 'CRC' ? 'USD' : normalized;

    try {
        const parts = new Intl.NumberFormat(resolveLocale(preferredLocale), {
            style: 'currency',
            currency: formattingCode,
            currencyDisplay: 'symbol'
        }).formatToParts(1);

        const currencyIndex = parts.findIndex(p => p.type === 'currency');
        const numberIndex = parts.findIndex(p => ['integer', 'fraction', 'group'].includes(p.type));

        if (currencyIndex === -1 || numberIndex === -1) return 'left';
        return currencyIndex < numberIndex ? 'left' : 'right';
    } catch {
        return 'left';
    }
}

export function formatCurrency(
    amount: number,
    currencyCode?: string | null,
    locale?: string | null,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
): string {
    return formatCurrencyForDisplay({
        amount,
        currencyCode,
        locale,
        minimumFractionDigits,
        maximumFractionDigits,
        showSymbol: true,
        symbolPosition: 'auto'
    }).formatted;
}

export function formatNumber(amount: number, locale?: string | null, minimumFractionDigits = 2, maximumFractionDigits = 2): string {
    const loc = resolveLocale(locale);
    try {
        return new Intl.NumberFormat(loc, {
            minimumFractionDigits,
            maximumFractionDigits
        }).format(amount);
    } catch {
        return amount.toFixed(Math.max(0, minimumFractionDigits));
    }
}

export function composeWithSymbol(
    amount: number,
    mappedSymbol?: string | null,
    currencyCode?: string | null,
    locale?: string | null,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
): string {
    const forceZeroDecimals = shouldUseZeroDecimalsForCountry();
    const normalizedAmount = forceZeroDecimals ? Math.round(amount) : amount;
    const normalizedMinDigits = forceZeroDecimals ? 0 : minimumFractionDigits;
    const normalizedMaxDigits = forceZeroDecimals ? 0 : maximumFractionDigits;
    const formattedNumber = formatNumber(normalizedAmount, locale, normalizedMinDigits, normalizedMaxDigits);
    const inferredCode = !currencyCode ? inferCurrencyCodeFromSymbol(mappedSymbol ?? undefined) : undefined;
    const normalizedCode = normalizeCurrencyCodeStrict(currencyCode ?? inferredCode) ?? DEFAULT_CURRENCY_CODE;
    let symbol = mappedSymbol ?? getCurrencySymbol(normalizedCode);
    if (!symbol) {
        symbol = getCurrencySymbol(DEFAULT_CURRENCY_CODE) || '€';
    }
    const pos: SymbolPosition = detectCurrencySymbolPosition(normalizedCode, locale);
    return pos === 'left' ? `${symbol}\u00A0${formattedNumber}` : `${formattedNumber}\u00A0${symbol}`;
}

// ============ NUEVA SECCIÓN: CURRENCY DISPLAY FORMAT ============

export interface CurrencyDisplayFormat {
    formatted: string;
    symbol: string;
    position: SymbolPosition;
    locale: string;
}

export interface CurrencyFormatOptions {
    amount: number;
    currencyCode?: string | null;
    country?: string | null;
    locale?: string | null;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showSymbol?: boolean;
    symbolPosition?: 'auto' | 'left' | 'right';
}

export function formatCurrencyForDisplay(
    options: CurrencyFormatOptions
): CurrencyDisplayFormat {
    const {
        amount,
        currencyCode,
        country,
        locale,
        minimumFractionDigits = 2,
        maximumFractionDigits = 2,
        showSymbol = true,
        symbolPosition = 'auto'
    } = options;

    const normalizedCode = normalizeCurrencyCodeStrict(currencyCode) ?? DEFAULT_CURRENCY_CODE;
    const symbol = getCurrencySymbol(normalizedCode);
    const resolvedLocale = resolveLocale(locale);
    const forceZeroDecimals = shouldUseZeroDecimalsForCountry(country);
    const normalizedAmount = forceZeroDecimals ? Math.round(amount) : amount;
    const normalizedMinDigits = forceZeroDecimals ? 0 : minimumFractionDigits;
    const normalizedMaxDigits = forceZeroDecimals ? 0 : maximumFractionDigits;

    const position = symbolPosition === 'auto'
        ? detectCurrencySymbolPosition(normalizedCode, locale)
        : (symbolPosition as SymbolPosition);

    const formattedNumber = formatNumber(normalizedAmount, locale, normalizedMinDigits, normalizedMaxDigits);

    const formatted = showSymbol
        ? (position === 'left'
            ? `${symbol}\u00A0${formattedNumber}`
            : `${formattedNumber}\u00A0${symbol}`)
        : formattedNumber;

    return {
        formatted,
        symbol,
        position,
        locale: resolvedLocale
    };
}

export function formatCurrencyValue(
    amount: number | null | undefined,
    options: Omit<CurrencyFormatOptions, 'amount'>
): string {
    if (amount === null || amount === undefined) {
        return '—';
    }
    if (!Number.isFinite(amount)) {
        return '—';
    }
    return formatCurrencyForDisplay({ ...options, amount }).formatted;
}

export function formatCurrencyNumber(
    amount: number | null | undefined,
    options: Omit<CurrencyFormatOptions, 'amount' | 'showSymbol'>
): string {
    if (amount === null || amount === undefined || !Number.isFinite(amount)) {
        return '—';
    }

    return formatCurrencyForDisplay({
        ...options,
        amount,
        showSymbol: false
    }).formatted;
}

export function formatCurrencyForPDF(
    options: CurrencyFormatOptions
): CurrencyDisplayFormat {
    const result = formatCurrencyForDisplay(options);
    return {
        ...result,
        formatted: result.formatted.replace(/\u00A0/g, ' ')
    };
}

/**
 * Rounds a currency amount respecting zero-decimal rules for TW/CL countries.
 * For TW/CL: rounds to integer (no decimals)
 * For other countries: rounds to 2 decimal places
 * @param value The value to round
 * @param country Optional country code to check for zero-decimal rules
 * @returns The rounded value
 */
export function roundCurrencyAmount(value: number, country?: string | null): number {
    const forceZeroDecimals = shouldUseZeroDecimalsForCountry(country);
    if (forceZeroDecimals) {
        return Math.round(value);
    }
    return Number(value.toFixed(2));
}

export const CurrencyUtils = {
    CURRENCY_SYMBOLS,
    getCurrencySymbol,
    registerCurrencySymbol,
    inferCurrencyCodeFromSymbol,
    normalizeCurrencyCode,
    normalizeCurrencyCodeStrict,
    isKnownCurrencyCode,
    resolveLocale,
    detectCurrencySymbolPosition,
    formatCurrency,
    formatNumber,
    composeWithSymbol,
    formatCurrencyForDisplay,
    formatCurrencyValue,
    formatCurrencyNumber,
    setCurrencyDisplayCountry,
    shouldUseZeroDecimalsForCountry,
    formatCurrencyForPDF,
    roundCurrencyAmount
};
